import { Link } from "react-router-dom";
import { ArrowRight, Gavel } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SCRIPTURES = [
  { id: "job", ref: "Job 22:28", text: "Thou shalt also decree a thing, and it shall be established unto thee: and the light shall shine upon thy ways." },
  { id: "isaiah", ref: "Isaiah 55:11", text: "So shall my word be that goeth forth out of my mouth: it shall not return unto me void, but it shall accomplish that which I please, and it shall prosper in the thing whereto I sent it." },
  { id: "mark", ref: "Mark 11:23", text: "Whosoever shall say unto this mountain, Be thou removed, and be thou cast into the sea; and shall not doubt in his heart, but shall believe that those things which he saith shall come to pass; he shall have whatsoever he saith." },
];

export function ScriptureFoundation() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="scripture-foundation-section">
      <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">The foundation</p>
      <h2 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight mt-3 max-w-2xl">Why we decree — and why it's established.</h2>
      <div className="grid md:grid-cols-3 gap-8 lg:gap-12 mt-12">
        {SCRIPTURES.map((s, i) => (
          <figure key={s.id} data-testid={`scripture-${s.id}`} className={i === 1 ? "md:mt-10" : ""}>
            <blockquote className="font-decree italic text-xl sm:text-2xl text-[#2C2A29] leading-snug">“{s.text}”</blockquote>
            <figcaption className="font-body text-xs uppercase tracking-[0.2em] text-[#9B7E4E] mt-4">{s.ref} · KJV</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

const FAQS = [
  { q: "How are the decrees created?", a: "You give a topic; we search Scripture for passages that speak to it and craft first-person, present-tense declarations woven around each verse. Every decree cites its reference so you can read it in context." },
  { q: "Are the decrees biblically sound?", a: "Each decree is anchored to a real verse and written in the language of Scripture rather than vague affirmations. The Scriptures Referenced panel under every set lets you read the full verse text, and the Bible reader lets you open it in nine translations." },
  { q: "What's included in the 7-day Premium trial?", a: "Everything: unlimited decrees and load-more, plans up to 40 days, all teachings, unlimited Lexicon word studies, all nine translations with highlights and read-aloud. You won't be charged if you cancel before the trial ends, and we email you two days before it converts." },
  { q: "Which Bible translations are available?", a: "KJV, World English Bible, ASV, Berean Standard Bible, Young's Literal, Darby, Bible in Basic English, Webster and Douay-Rheims. Guests can read three; Premium unlocks all nine." },
  { q: "Can I gift Premium to a friend?", a: "Yes. From the Newsfeed, a member's profile or the Community page, tap Gift Premium — a one-time $4.99 gives them 30 days of Premium and posts your blessing to the Newsfeed." },
  { q: "Do I need an account?", a: "No. Guests can generate five decrees per topic, read the Bible and share. A free account unlocks full 10–15 decree sets, saved history, plans and the Newsfeed." },
];

export function FAQ() {
  return (
    <section className="bg-[#F3F0EA]/60 border-y border-[#EAE6DF]" data-testid="faq-section">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">Questions</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight mt-3">Asked often.</h2>
        </div>
        <Accordion type="single" collapsible className="lg:col-span-8">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-[#D9D4CC]">
              <AccordionTrigger className="font-heading text-lg sm:text-xl text-[#2C2A29] text-left hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="font-body text-base text-[#4A4744] leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="final-cta-section">
      <div className="border border-[#9B7E4E]/50 bg-[#FDFBF7] rounded-sm px-8 sm:px-12 py-12 sm:py-16 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-8">
          <Gavel className="w-7 h-7 text-[#9B7E4E]" strokeWidth={1.5} />
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#2C2A29] tracking-tight mt-5 leading-[1.1]">Start establishing God's promises today.</h2>
          <p className="font-decree text-xl text-[#4A4744] mt-4">Free to begin. Seven days of Premium on us.</p>
        </div>
        <div className="lg:col-span-4 flex flex-col gap-3">
          <Link to="/auth" data-testid="final-cta-signup-button" className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300">
            Create a free account <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/generate" data-testid="final-cta-guest-button" className="inline-flex items-center justify-center px-6 py-4 border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300">
            Try without an account
          </Link>
        </div>
      </div>
    </section>
  );
}
